// src/pages/Team/OwnerDisciplines.jsx
// STATUS: active | UPDATED: 2026-09-27
// LOG:
//   2026-09-27  Reads moved to src/lib/storeWeek.js, shared with the owner's
//               weekly check-in on Today.
//   2026-09-24  New. The owner could see one manager action and there are now
//               four.
//
// IS THE PROCESS BEING RUN.
//
// A DMS can tell an owner what sold. Nothing can tell them whether the morning
// meeting happened, and on the 2026-09-24 research pass that turned out to be
// the whole commercial argument: the trade's own read is that execution in 2026
// depends on manager-led training rather than tools, and that the old way new
// people learned, by watching somebody experienced, is broken with nothing
// having replaced it.
//
// So this is four rows and a sentence. Readable in half a minute, because the
// same research names "reduced time out of role" as the thing that decides
// whether a dealer adopts anything at all.
//
// It states facts and never draws the conclusion. "The morning meeting ran on
// two of six working days" is something an owner can take into a conversation.
// "Your manager is not doing their job" is not ours to say and would be wrong
// the week somebody was on holiday.

import { useCallback, useEffect, useState } from 'react'
import { Box, HStack, VStack, Text, Spinner, Alert, AlertIcon } from '@chakra-ui/react'
import { useAuth } from '../../hooks/useAuth'
import { ownerHeadline, toneFor, WINDOW_DAYS } from '../../lib/ownerWeek'
import { loadStoreWeek } from '../../lib/storeWeek'

const TONE = { good: 'success', partial: 'warn', none: 'danger', quiet: 'inkDim' }

export default function OwnerDisciplines() {
  const { profile } = useAuth()
  const dealershipId = profile?.dealership_id || null
  const [state, setState] = useState({ loading: true, rows: [], floorSize: 0, error: null })

  const load = useCallback(async () => {
    if (!dealershipId) {
      setState({ loading: false, rows: [], floorSize: 0, error: null })
      return
    }
    // The reads live in storeWeek.js so the owner's check-in on Today and
    // this page cannot disagree about the same week. The audit read that was
    // once missing from the error check is guarded there.
    const week = await loadStoreWeek(dealershipId)
    setState({ loading: false, rows: week.rows, floorSize: week.floorSize, error: week.error })
  }, [dealershipId])

  useEffect(() => {
    load()
  }, [load])

  if (state.loading) return <Spinner size="sm" />
  if (!dealershipId) return null

  return (
    <Box>
      <Text fontSize="lg" fontWeight={600} mb={0.5}>
        Is the process being run
      </Text>
      <Text fontSize="body" color="inkMuted" mb={3} maxW="measure" lineHeight={1.55}>
        {ownerHeadline(state.rows, state.floorSize)} Last {WINDOW_DAYS} days. Your DMS can tell you
        what sold. This is the part it cannot see.
      </Text>

      {state.error && (
        <Alert status="warning" borderRadius="md" mb={3}>
          <AlertIcon />
          {state.error}
        </Alert>
      )}

      <VStack align="stretch" spacing={0}>
        {state.rows.map((r) => (
          <HStack
            key={r.key}
            py={3}
            borderTop="1px solid"
            borderColor="line"
            justify="space-between"
            align="baseline"
            spacing={4}
          >
            <Text fontSize="body">{r.label}</Text>
            <HStack spacing={2} align="baseline" flexShrink={0}>
              <Text fontFamily="mono" fontSize="lg" fontWeight={600} color={TONE[toneFor(r)]}>
                {r.count}
              </Text>
              <Text fontFamily="mono" fontSize="sm" color="inkDim" whiteSpace="nowrap">
                {r.of == null ? r.unit : `of ${r.of} ${r.unit}`}
              </Text>
            </HStack>
          </HStack>
        ))}
      </VStack>

      <Text fontSize="sm" color="inkDim" mt={3} maxW="measure" lineHeight={1.6}>
        These are four of the six daily disciplines Jazz teaches. The other two, appointment
        confirmation and unsold customer review, happen in your CRM and are not visible from here.
      </Text>
    </Box>
  )
}
